/**
 * Shared-Wheel -> clone return primitive (QA-148 #6, web parts).
 *
 * The ideal shared-Wheel journey is:
 *   Shared Wheel link -> View Wheel -> (Get App / Sign Up) -> auto-return to the
 *   SAME Wheel -> an OBVIOUS "Clone this Wheel" CTA.
 *
 * The web flow used to LOSE the origin Wheel the moment a guest left View Wheel
 * to authenticate: the clone dialog's guest branch did a bare `nav("/login")`
 * and Signup always landed on `nav("/gallery")`, so a visitor who arrived on a
 * shared Wheel specifically to clone it was dumped on a generic gallery, with no
 * memory of what they came for.
 *
 * `utils/loginRedirect.js` already carries the origin as a `?redirect=` URL
 * param across the LOGIN round trip. That param does NOT survive the SIGNUP path
 * (Signup navigates with `window.location.href` and, for email signup, detours
 * through email verification before the user ever reaches /login). So this module
 * adds a durable, browser-local memory of the origin Wheel that survives BOTH the
 * login and the signup (incl. email-verify) detours: two short-lived localStorage
 * keys, read back when the user returns authenticated.
 *
 * All of this is gated by SHARED_CLONE_RETURN_ENABLED at every call site; this
 * module only provides the primitive (and is pure/testable). When the flag is
 * OFF nothing here is ever called, so the flows are byte-identical to today.
 */
import { safeLoginRedirectTarget } from "./loginRedirect";

// Where to send the user back once they authenticate (a same-origin path).
export const RETURN_KEY = "iyugaPostAuthReturn";
// Which Wheel the user intended to clone (drives the obvious CTA on return).
export const INTENT_KEY = "iyugaCloneIntentWheelId";
// The email the guest typed into the download / "Get the app" prompt (QA-148 #3).
// Persisted so the web Signup form can PRE-POPULATE its email field instead of
// making the guest retype the address they already gave one screen earlier.
export const EMAIL_KEY = "iyugaDownloadFlowEmail";

// The memory is a convenience, not a durable contract: expire it so a stale
// intent from days ago never hijacks an unrelated later signup.
export const TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

function store() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  return window.localStorage;
}

function now() {
  return Date.now();
}

/**
 * Remember a same-origin path to return to once the visitor authenticates —
 * the shared primitive both the clone-intent flow below and
 * {@link rememberPostAuthReturnPath} write through. No-op without
 * localStorage or an unsafe path (see {@link safeLoginRedirectTarget}).
 */
function rememberReturnPath(returnPath) {
  const ls = store();
  if (!ls) return;
  const safePath = safeLoginRedirectTarget(returnPath);
  if (!safePath) return;
  try {
    ls.setItem(RETURN_KEY, JSON.stringify({ path: safePath, ts: now() }));
  } catch (e) {
    // storage full / disabled -> the flow simply falls back to today's behaviour
  }
}

/**
 * Remember the origin Wheel a guest is about to leave (to auth) intending to
 * clone it. Safe to call in any browser context; a no-op without localStorage.
 *
 * @param {string} returnPath the current same-origin path (e.g. "/wheel/512?key=..")
 * @param {string|number} wheelId the origin Wheel's id
 */
export function rememberSharedCloneOrigin(returnPath, wheelId) {
  rememberReturnPath(returnPath);
  const ls = store();
  if (!ls) return;
  if (wheelId !== undefined && wheelId !== null && `${wheelId}` !== "") {
    try {
      ls.setItem(INTENT_KEY, JSON.stringify({ wheelId: `${wheelId}`, ts: now() }));
    } catch (e) {
      // storage full / disabled -> the flow simply falls back to today's behaviour
    }
  }
}

/**
 * stgfix-1007-P0-phone-edit-wheel-funnel-to-desktop (Gordon addendum
 * 2026-10-07): remember a protected page a LOGGED-OUT visitor was trying to
 * reach (today: a Collaboration or Team invite accept page) so they land back
 * on it once authenticated — surviving not just the login round trip (which
 * react-router `location.state` already covers, see PrivateRoute.js) but also
 * a brand-new visitor's SIGNUP detour, including email verification, which
 * leaves the SPA entirely and cannot carry router state. Same durable,
 * single-use, 6h-TTL memory as the clone-return flow above (consumed by the
 * same {@link consumePostAuthReturnPath}, already called from every signup
 * completion path — email/password via `proceedToApp`, and each social
 * login).
 *
 * @param {string} returnPath the current same-origin path+search (e.g.
 *   "/collaboration/accept?token=..")
 */
export function rememberPostAuthReturnPath(returnPath) {
  rememberReturnPath(returnPath);
}

function readFresh(key) {
  const ls = store();
  if (!ls) return null;
  try {
    const parsed = JSON.parse(ls.getItem(key));
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.ts !== "number" || now() - parsed.ts > TTL_MS) {
      ls.removeItem(key);
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

/**
 * The post-auth destination: the remembered origin-Wheel path, validated as a
 * safe same-origin path, then CLEARED (single use). Returns null when there is
 * nothing remembered / it expired / it is not a safe path.
 *
 * @returns {string|null}
 */
export function consumePostAuthReturnPath() {
  const rec = readFresh(RETURN_KEY);
  const ls = store();
  if (ls) {
    try {
      ls.removeItem(RETURN_KEY);
    } catch (e) {
      /* ignore */
    }
  }
  if (!rec) return null;
  return safeLoginRedirectTarget(rec.path);
}

/**
 * The Wheel the returning user intended to clone (for the obvious CTA). Does NOT
 * clear it, because the CTA may render across several renders before the user
 * acts; call {@link clearCloneIntent} once the clone is done or dismissed.
 *
 * @returns {string|null} the wheel id as a string, or null
 */
export function getCloneIntentWheelId() {
  const rec = readFresh(INTENT_KEY);
  return rec ? rec.wheelId : null;
}

/**
 * Remember the email a guest typed into the download / "Get the app" prompt so
 * the web Signup form can pre-populate it (QA-148 #3). No-op without a real
 * email or localStorage; same 6h TTL as the rest of the funnel memory.
 *
 * @param {string} email
 */
export function rememberDownloadFlowEmail(email) {
  const ls = store();
  if (!ls) return;
  const clean = typeof email === "string" ? email.trim() : "";
  if (!clean) return;
  try {
    ls.setItem(EMAIL_KEY, JSON.stringify({ email: clean, ts: now() }));
  } catch (e) {
    // storage full / disabled -> Signup simply starts with a blank email
  }
}

/**
 * The download-flow email to seed the Signup form with, then CLEAR (single use,
 * mirrors {@link consumePostAuthReturnPath}). Returns null when nothing fresh
 * was remembered.
 *
 * @returns {string|null}
 */
export function consumeDownloadFlowEmail() {
  const rec = readFresh(EMAIL_KEY);
  const ls = store();
  if (ls) {
    try {
      ls.removeItem(EMAIL_KEY);
    } catch (e) {
      /* ignore */
    }
  }
  return rec ? rec.email : null;
}

/**
 * Pure seam the Signup form uses for its initial email value: the remembered
 * download-flow email when the funnel flag is on, else "" (byte-identical to
 * today when off). Single-use — it consumes the memory.
 *
 * @param {boolean} enabled SHARED_CLONE_RETURN_ENABLED
 * @returns {string}
 */
export function initialSignupEmail(enabled) {
  if (!enabled) return "";
  return consumeDownloadFlowEmail() || "";
}

/** Forget the clone intent (after the CTA is acted on or dismissed). */
export function clearCloneIntent() {
  const ls = store();
  if (!ls) return;
  try {
    ls.removeItem(INTENT_KEY);
  } catch (e) {
    /* ignore */
  }
}

/**
 * Pure decision: should the obvious "Clone this Wheel" CTA render on this Wheel?
 * True only for a signed-in viewer whose remembered clone-intent Wheel matches
 * the Wheel currently being viewed (and only while the flag is on).
 *
 * @param {{enabled:boolean,isLoggedIn:boolean,intentWheelId:(string|null),currentWheelId:(string|number|null|undefined)}} args
 * @returns {boolean}
 */
export function shouldShowSharedCloneCTA({
  enabled,
  isLoggedIn,
  intentWheelId,
  currentWheelId,
}) {
  if (!enabled || !isLoggedIn) return false;
  if (!intentWheelId || currentWheelId === undefined || currentWheelId === null)
    return false;
  return `${intentWheelId}` === `${currentWheelId}`;
}
